import React from "react";
import axiosInstance from "./axiosInstance";
import { getUrlForUsers } from "./confighelpers";

/**
 * Club link = the club's member address <link>.<root> (users.myBookingLink). Rules (same as the backend,
 * FitClub-Backend/src/functions/users/utils.js): a-z, 0-9 and single dashes, 3–30 characters, starting and ending
 * with a letter or number; reserved names and links of other clubs (now or before) are refused by the server.
 * A club picks it once, on the club details step of onboarding. After that only the platform changes it
 * (rebrand on request by email, backend scripts/change-club-link.js; old links keep redirecting).
 */

export const CLUB_LINK_MAX = 30;
export const PLATFORM_SUPPORT_EMAIL = "info@bookzy.nl";

/** Why the typed link can't be used ("" when the format is fine). Reserved / taken come from the server. */
export function clubLinkProblem(value) {
  const v = String(value || "").trim().toLowerCase();
  if (!v) return "Please choose a club link.";
  if (!/^[a-z0-9-]+$/.test(v)) return "Use only letters (a–z), numbers and dashes. No spaces or accents.";
  if (v.length < 3) return "Use at least 3 characters.";
  if (v.length > CLUB_LINK_MAX) return `Use ${CLUB_LINK_MAX} characters or fewer.`;
  if (v.startsWith("-") || v.endsWith("-")) return "It can't start or end with a dash.";
  if (v.indexOf("--") >= 0) return "Use single dashes only.";
  return "";
}

/** A link suggestion from the club name: "Studio Noörd & Co!" → "studio-noord-co" (same as the backend). */
export function suggestClubLink(name) {
  let slug = String(name || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  if (slug.length > CLUB_LINK_MAX) {
    const cut = slug.slice(0, CLUB_LINK_MAX + 1);
    const lastDash = cut.lastIndexOf("-");
    slug = (lastDash >= 3 ? cut.slice(0, lastDash) : slug.slice(0, CLUB_LINK_MAX)).replace(/-+$/g, "");
  }
  if (!slug) return "";
  return slug.length < 3 ? `${slug}-club` : slug;
}

/** Only what a link may contain, while typing (lower-case, no spaces or other characters). */
export const cleanClubLinkInput = (value) => String(value || "").toLowerCase().replace(/[^a-z0-9-]/g, "").slice(0, CLUB_LINK_MAX);

/** One server check: POST users/verifyBusinessDomain → { state: "ok" | "taken" | "error", message, link }. */
export async function checkClubLink(value) {
  const v = String(value || "").trim().toLowerCase();
  const problem = clubLinkProblem(v);
  if (problem) return { state: "invalid", message: problem, link: v };
  try {
    const res = await axiosInstance({ method: "post", url: `${getUrlForUsers()}verifyBusinessDomain`, data: { myBookingLink: v } });
    const d = res?.data;
    if (d?.statusCode === 200) return { state: "ok", message: "", link: d.data?.myBookingLink || v };
    if (d?.statusCode === 403) return { state: "taken", message: d.message?.message || (typeof d.message === "string" ? d.message : "This link is already taken."), link: v };
    return { state: "error", message: typeof d?.message === "string" ? d.message : "Couldn't check this link.", link: v };
  } catch (e) {
    return { state: "error", message: "Couldn't check this link. Please check your connection.", link: v };
  }
}

/** Checks the link while typing (format at once, server after 500 ms). state: idle | invalid | checking | ok | taken | error */
export function useClubLinkCheck(value) {
  const [check, setCheck] = React.useState({ state: "idle", message: "", link: "" });
  const reqRef = React.useRef(0);
  React.useEffect(() => {
    const v = String(value || "").trim().toLowerCase();
    const id = ++reqRef.current;
    if (!v) {
      setCheck({ state: "idle", message: "", link: "" });
      return undefined;
    }
    const problem = clubLinkProblem(v);
    if (problem) {
      setCheck({ state: "invalid", message: problem, link: v });
      return undefined;
    }
    setCheck({ state: "checking", message: "", link: v });
    const t = setTimeout(async () => {
      const r = await checkClubLink(v);
      if (id === reqRef.current) setCheck(r);
    }, 500);
    return () => clearTimeout(t);
  }, [value]);
  return check;
}

/** The small status badge next to the field. */
export function ClubLinkBadge({ state }) {
  if (state === "checking")
    return (
      <span className="flex items-center gap-1.5 whitespace-nowrap text-xs font-bold text-gray-500">
        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-bz" />
        Checking…
      </span>
    );
  if (state === "ok") return <span className="whitespace-nowrap rounded-full bg-[#E8F7EE] px-2.5 py-0.5 text-xs font-extrabold text-[#1F6B3A]">Available</span>;
  if (state === "taken" || state === "invalid") return <span className="whitespace-nowrap rounded-full bg-[#FFE8EB] px-2.5 py-0.5 text-xs font-extrabold text-[#B4232F]">Not available</span>;
  return null;
}
